import { useQuery } from 'react-query';
import { useProjectId } from './useProjectId';
import { useUserSettings } from './api-hooks/useUserSettings';
import { getModuleAdmins } from '~src/services/api/modules';

export interface UseIsModuleFileAdminOptions {
  /**
   * `GET /project_settings/{project_id}/module_admins` requires
   * superuser-level access - non-superusers (e.g. Manager accounts) always
   * get a 403 from it. Callers must explicitly opt in (typically with
   * `isSuperuser`) so this never fires for users who can't call it anyway.
   * Defaults to `true` to preserve existing call sites that don't pass it.
   */
  enabled?: boolean;
}

/**
 * Hook to check if current user has module_file_admin permission
 *
 * Users with this permission see the new unified Sectors browser.
 * Users without this permission see the old module navigation (Tasks, TQ, etc.)
 *
 * @returns boolean indicating if user has module_file_admin access
 */
export const useIsModuleFileAdmin = (
  options?: UseIsModuleFileAdminOptions
): boolean => {
  const projectId = useProjectId();
  const userSettings = useUserSettings();

  const { data: moduleAdmins } = useQuery(
    ['moduleAdmins', projectId],
    () => getModuleAdmins({ projectId: String(projectId) }),
    {
      enabled: !!projectId && (options?.enabled ?? true),
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
    }
  );

  const currentUserId = userSettings.data?.user_id;

  if (!currentUserId || !moduleAdmins) {
    return false;
  }

  return moduleAdmins.some((admin) => admin.id === currentUserId);
};

/**
 * Hook to check if current user has module_file_admin permission, while also
 * exposing the loading state of the underlying queries.
 *
 * Unlike `useIsModuleFileAdmin`, this hook does NOT conflate "loading" with
 * "not admin" - consumers can use `isLoading` to avoid making a rendering
 * decision before the admin status is actually known (e.g. on a hard reload).
 *
 * @returns object with `isModuleAdmin` boolean and `isLoading` boolean
 */
export const useIsModuleFileAdminStatus = (
  options?: UseIsModuleFileAdminOptions
): {
  isModuleAdmin: boolean;
  isLoading: boolean;
} => {
  const projectId = useProjectId();
  const userSettings = useUserSettings();

  const moduleAdminsQuery = useQuery(
    ['moduleAdmins', projectId],
    () => getModuleAdmins({ projectId: String(projectId) }),
    {
      enabled: !!projectId && (options?.enabled ?? true),
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
    }
  );

  const currentUserId = userSettings.data?.user_id;
  const moduleAdmins = moduleAdminsQuery.data;
  const isModuleAdmin =
    !!currentUserId && !!moduleAdmins
      ? moduleAdmins.some((admin) => admin.id === currentUserId)
      : false;

  return {
    isModuleAdmin,
    isLoading: moduleAdminsQuery.isLoading || userSettings.isLoading,
  };
};
